import { MarkerIcon } from "../shared/markers";
import { hasUiIcon, UiIcon } from "./Icon";
import { strings } from "./i18n";
import type { Lang, PartnerInfo } from "./types";
import styles from "./PartnerMap.module.scss";

interface InfoLinksProps {
	info: PartnerInfo;
	/** the short name: "Mad Monkey on Instagram" */
	shortName: string;
	/** the ids of the links start with this: the block is on the page twice, in the list and in the panel */
	idPrefix: string;
	/** the address line over the links; off where the address is shown already */
	withAddress?: boolean;
	lang?: Lang;
	onContact: (channel: string) => void;
}

/**
 * Where to book and follow the partner: the links it has (a missing one is left out), its address as text and its socials,
 * drawn with the networks' own logos (the four in uiIcons.json; any other network gets the globe).
 * The block that ends the Highlights tab, and the body of the "Book & info" panel.
 */
const InfoLinks = ({ info, shortName, idPrefix, withAddress = true, lang, onContact }: InfoLinksProps) => {
	const t = strings(lang);
	const links = [
		info.book && { key: "book", href: info.book, label: t.bookBed, icon: <MarkerIcon name="bed" size={18} />, book: true },
		info.events && { key: "events", href: info.events, label: t.allEvents, icon: <UiIcon name="tabWeek" /> },
		info.website && { key: "website", href: info.website, label: t.hostelPage, icon: <UiIcon name="globe" /> },
		info.directions && { key: "directions", href: info.directions, label: t.directions, icon: <UiIcon name="directions" /> },
	].filter((link): link is NonNullable<typeof link> => !!link);
	return (
		<>
			{withAddress && info.address && <p className={styles.addr}>{info.address}</p>}
			{links.length > 0 && (
				<div className={styles.quick} style={{ gridTemplateColumns: `repeat(${links.length}, 1fr)` }}>
					{links.map((link) => (
						<a
							key={link.key}
							id={`${idPrefix}-${link.key}`}
							className={link.book ? styles.book : undefined}
							href={link.href}
							target="_blank"
							rel="noopener"
							onClick={() => onContact(link.key)}
						>
							<i>{link.icon}</i>
							{link.label}
						</a>
					))}
				</div>
			)}
			{info.socials.length > 0 && (
				<div className={styles.socials}>
					<span className={styles.follow}>
						<b>{info.followLabel}</b>
						{info.followHandle && <small>{info.followHandle}</small>}
					</span>
					<div className={styles.nets}>
						{info.socials.map(({ network, url }) => {
							const key = network.toLowerCase();
							return (
								<a
									key={network}
									id={`${idPrefix}-${key}`}
									href={url}
									target="_blank"
									rel="noopener"
									aria-label={t.onNetwork(shortName, network)}
									title={network}
									onClick={() => onContact(key)}
								>
									<UiIcon name={hasUiIcon(key) ? key : "globe"} />
								</a>
							);
						})}
					</div>
				</div>
			)}
		</>
	);
};

export default InfoLinks;
